import { backendClient } from '../lib/api/backendClient';

export type MutedEntityType = 'composer' | 'work' | 'artist' | 'recording';

export type MutedEntity = {
  entityType: MutedEntityType;
  entityId: string;
  mutedAt: string;
  notes?: string | null;
};

export type ListMutesResponse = {
  mutes?: Array<Record<string, unknown>>;
};

const parseMutedEntity = (payload: Record<string, unknown>): MutedEntity | null => {
  const entityType = typeof payload.entityType === 'string' ? payload.entityType.trim() : '';
  const entityId = typeof payload.entityId === 'string' ? payload.entityId.trim() : '';
  const mutedAt = typeof payload.mutedAt === 'string' ? payload.mutedAt.trim() : '';

  if (!entityType || !entityId || !mutedAt) {
    return null;
  }

  const normalizedEntityType = entityType.toLowerCase();
  if (!['composer', 'work', 'artist', 'recording'].includes(normalizedEntityType)) {
    return null;
  }

  return {
    entityType: normalizedEntityType as MutedEntityType,
    entityId,
    mutedAt,
    notes:
      typeof payload.notes === 'string' && payload.notes.trim().length > 0
        ? payload.notes.trim()
        : undefined
  };
};

export const mutesService = {
  async listMutes(): Promise<MutedEntity[]> {
    const { data } = await backendClient.get<ListMutesResponse>('/mutes');
    const entries = Array.isArray(data?.mutes) ? data.mutes : [];
    const normalized: MutedEntity[] = [];
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue;
      const parsed = parseMutedEntity(entry as Record<string, unknown>);
      if (parsed) {
        normalized.push(parsed);
      }
    }
    return normalized;
  },

  async muteEntity(input: {
    entityType: MutedEntityType;
    entityId: string;
    source?: string;
    notes?: string | null;
  }): Promise<void> {
    const payload: Record<string, unknown> = {
      entityType: input.entityType,
      entityId: input.entityId
    };
    if (typeof input.source === 'string' && input.source.trim().length > 0) {
      payload.source = input.source.trim();
    }
    if (typeof input.notes === 'string') {
      const trimmed = input.notes.trim();
      if (trimmed.length > 0) {
        payload.notes = trimmed;
      }
    }
    await backendClient.post('/mutes', payload);
  },

  async unmuteEntity(
    entityType: MutedEntityType,
    entityId: string,
    options?: { source?: string }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.source && options.source.trim().length > 0) {
      params.set('source', options.source.trim());
    }
    const suffix = params.toString().length > 0 ? `?${params.toString()}` : '';
    await backendClient.delete(`/mutes/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${suffix}`);
  }
};

export type MutesService = typeof mutesService;
